import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import type { FieldValues, UseControllerProps } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { formatUserName } from "client/api/UserGrantApi";
import { getUsers } from "client/api/UsersApi";
import { FilterCombinationType } from "client/api/common/FilterQuery";
import { EntitySelector } from "../EntitySelector/EntitySelector";

interface UserSelectorFieldNames {
  user: string;
  firstName: string;
  lastName: string;
  email: string;
}

const defaultFieldNames: UserSelectorFieldNames = {
  user: "user",
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
};

type UserSelectorProps<T extends FieldValues> = UseControllerProps<T> & {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  allowCreate?: boolean;
  onModeChange?: (mode: "search" | "create") => void;
  fieldNames?: UserSelectorFieldNames;
};

const searchResultLimit = 20;

function searchUsers(searchTerm?: string) {
  if (!searchTerm) {
    return getUsers({ skip: 0, take: searchResultLimit });
  }
  return getUsers({
    skip: 0,
    take: searchResultLimit,
    filterCombinationType: FilterCombinationType.Or,
    filter: { email: { like: searchTerm }, firstName: { like: searchTerm }, lastName: { like: searchTerm } },
  });
}

export function UserSelector<T extends FieldValues>(props: UserSelectorProps<T>) {
  const { label, required, allowCreate, onModeChange, fieldNames, ...rest } = props;
  const fields = fieldNames ?? defaultFieldNames;
  const [mode, setModeInternal] = useState<"search" | "create">("search");
  const { register, setValue, formState } = useFormContext();

  const setMode = (newMode: "search" | "create") => {
    setModeInternal(newMode);
    onModeChange?.(newMode);
  };

  useEffect(() => {
    if (!allowCreate && mode === "create") {
      setMode("search");
    }
  }, [allowCreate, mode]);

  const handleEnterCreateMode = () => {
    setValue(fields.user, undefined);
    setMode("create");
  };

  const enterCreateModeRef = useRef(handleEnterCreateMode);
  enterCreateModeRef.current = handleEnterCreateMode;

  const renderFooter = useCallback(
    () => (
      <>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Type to search and see more results
          </Typography>
        </Box>
        {allowCreate && (
          <Button
            variant="text"
            fullWidth
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={() => enterCreateModeRef.current()}
            sx={{ justifyContent: "flex-start", px: 2, py: 1 }}
          >
            + Create new user
          </Button>
        )}
      </>
    ),
    [allowCreate]
  );

  const handleBackToSearch = () => {
    setValue(fields.firstName, undefined);
    setValue(fields.lastName, undefined);
    setValue(fields.email, undefined);
    setMode("search");
  };

  if (allowCreate && mode === "create") {
    return (
      <div>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Grid>
            <Button variant="text" onClick={handleBackToSearch}>
              &larr; Back to search
            </Button>
          </Grid>
          <Grid size="grow">
            <TextField
              {...register(fields.firstName, { required: "First name is required" })}
              label="First Name"
              required
              fullWidth
              variant="outlined"
              error={!!formState.errors?.[fields.firstName]}
              helperText={String(formState.errors?.[fields.firstName]?.message ?? "")}
            />
          </Grid>
          <Grid size="grow">
            <TextField
              {...register(fields.lastName, { required: "Last name is required" })}
              label="Last Name"
              required
              fullWidth
              variant="outlined"
              error={!!formState.errors?.[fields.lastName]}
              helperText={String(formState.errors?.[fields.lastName]?.message ?? "")}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              {...register(fields.email, {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email address",
                },
              })}
              label="Email"
              required
              fullWidth
              variant="outlined"
              error={!!formState.errors?.[fields.email]}
              helperText={String(formState.errors?.[fields.email]?.message ?? "")}
            />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <EntitySelector
      {...rest}
      label={label || "User"}
      apiRequest={searchUsers}
      optionLabelFunc={formatUserName}
      required={required}
      renderFooter={renderFooter}
    />
  );
}
